/**
 * Repository Implementation: Programs
 *
 * Bridges the ProgramsApiService with the domain model.
 */

import { inject, injectable } from "inversify";

import type {
  FetchProgramsParams,
  ProductUploadResult,
  ProgramDetail,
  ProgramItem,
  ProgramsPaginatedResult,
  ProgramWritePayload,
  WhitelistUploadResult,
} from "../../domain/model/ProgramsModel";
import type { IProgramsRepository } from "../../domain/repository/IProgramsRepository";
import type { ProgramListItemDto } from "../remote/response/ProgramsResponse";
import { ProgramsApiService } from "../remote/service/ProgramsApiService";

@injectable()
export class ProgramsRepositoryImpl implements IProgramsRepository {
  @inject(ProgramsApiService)
  private readonly apiService!: ProgramsApiService;

  async fetchAll(): Promise<readonly ProgramItem[]> {
    const response = await this.apiService.fetchProgramsPaginated({
      page: 1,
      pageSize: 50,
    });

    return response.data.map((dto) => this.mapDtoToItem(dto));
  }

  async fetchPaginated(
    params: FetchProgramsParams,
  ): Promise<ProgramsPaginatedResult> {
    const response = await this.apiService.fetchProgramsPaginated(params);

    return {
      items: response.data.map((dto) => this.mapDtoToItem(dto)),
      paging: {
        currentPage: response.paging.currentPage,
        pageSize: response.paging.pageSize,
        totalRecords: response.paging.totalRecords,
        totalPages: response.paging.totalPages,
      },
    };
  }

  async fetchDetail(programId: string): Promise<ProgramDetail> {
    const dto = await this.apiService.fetchProgramDetail(programId);

    return {
      programId: dto.program.programId,
      name: dto.program.name,
      programName: dto.program.programName,
      startDate: dto.program.startDate,
      endDate: dto.program.endDate,
      status: dto.program.status,
      termsAndConditions: dto.program.termsAndConditions,
      howItWorks: dto.program.howItWorks,
      incentiveRule: {
        type: dto.incentiveRule.type,
        basedOn: dto.incentiveRule.basedOn,
        minimumTransaction: dto.incentiveRule.minimumTransaction,
        maximumTransaction: dto.incentiveRule.maximumTransaction,
        totalProducts: dto.incentiveRule.totalProducts,
        maxBaseRewardPerDay: dto.incentiveRule.maxBaseRewardPerDay,
        baseRewards: dto.incentiveRule.baseRewards,
        stampMultiplier: dto.incentiveRule.stampMultiplier,
      },
      customerSegment: {
        areas: dto.customerSegment.areas,
        tiers: dto.customerSegment.tiers,
        whitelistCount: dto.customerSegment.whitelistCount,
      },
    };
  }

  async create(payload: ProgramWritePayload): Promise<string> {
    const result = await this.apiService.createProgram({
      program: {
        name: payload.program.name,
        programName: payload.program.programName,
        startDate: payload.program.startDate,
        endDate: payload.program.endDate,
        status: payload.program.status,
        termsAndConditions: payload.program.termsAndConditions
          ? [...payload.program.termsAndConditions]
          : undefined,
        howItWorks: payload.program.howItWorks
          ? [...payload.program.howItWorks]
          : undefined,
      },
      incentiveRule: {
        type: payload.incentiveRule.type,
        basedOn: payload.incentiveRule.basedOn,
        minimumTransaction: payload.incentiveRule.minimumTransaction,
        maximumTransaction: payload.incentiveRule.maximumTransaction,
        productTemporaryFileId: payload.incentiveRule.productTemporaryFileId,
        maxBaseRewardPerDay: payload.incentiveRule.maxBaseRewardPerDay,
        baseRewards: payload.incentiveRule.baseRewards
          ? [...payload.incentiveRule.baseRewards]
          : undefined,
        stampMultiplier: payload.incentiveRule.stampMultiplier,
      },
      customerSegment: {
        areas: [...payload.customerSegment.areas],
        tiers: [...payload.customerSegment.tiers],
        whitelistTemporaryId: payload.customerSegment.whitelistTemporaryId,
      },
    });

    return result.programId;
  }

  async update(programId: string, payload: ProgramWritePayload): Promise<void> {
    await this.apiService.updateProgram(programId, {
      program: {
        name: payload.program.name,
        programName: payload.program.programName,
        startDate: payload.program.startDate,
        endDate: payload.program.endDate,
        status: payload.program.status,
        termsAndConditions: payload.program.termsAndConditions
          ? [...payload.program.termsAndConditions]
          : undefined,
        howItWorks: payload.program.howItWorks
          ? [...payload.program.howItWorks]
          : undefined,
      },
      incentiveRule: {
        type: payload.incentiveRule.type,
        basedOn: payload.incentiveRule.basedOn,
        minimumTransaction: payload.incentiveRule.minimumTransaction,
        maximumTransaction: payload.incentiveRule.maximumTransaction,
        productTemporaryFileId: payload.incentiveRule.productTemporaryFileId,
        maxBaseRewardPerDay: payload.incentiveRule.maxBaseRewardPerDay,
        baseRewards: payload.incentiveRule.baseRewards
          ? [...payload.incentiveRule.baseRewards]
          : undefined,
        stampMultiplier: payload.incentiveRule.stampMultiplier,
      },
      customerSegment: {
        areas: [...payload.customerSegment.areas],
        tiers: [...payload.customerSegment.tiers],
        whitelistTemporaryId: payload.customerSegment.whitelistTemporaryId,
      },
    });
  }

  async delete(programId: string): Promise<void> {
    await this.apiService.deleteProgram(programId);
  }

  async uploadWhitelistFile(file: File): Promise<WhitelistUploadResult> {
    const dto = await this.apiService.uploadWhitelistFile(file);

    return {
      totalCustomers: dto.totalCustomers,
      preview: dto.preview.map((item) => ({
        customerId: item.customerId,
        name: item.name,
        tier: item.tier,
      })),
      temporaryFileId: dto.temporaryFileId,
    };
  }

  async uploadProductFile(file: File): Promise<ProductUploadResult> {
    const dto = await this.apiService.uploadProductFile(file);

    return {
      totalProducts: dto.totalProducts,
      preview: dto.preview.map((item) => ({
        no: item.no,
        productId: item.productId,
        productName: item.productName,
        pointIncentive: item.pointIncentive,
      })),
      temporaryFileId: dto.temporaryFileId,
    };
  }

  private mapDtoToItem(dto: ProgramListItemDto): ProgramItem {
    return {
      id: dto.programId,
      programId: dto.programId,
      name: dto.name,
      programName: dto.programName,
      status: dto.status,
      startDate: dto.startDate,
      endDate: dto.endDate,
      incentiveType: dto.incentiveType,
      basedOn: dto.basedOn,
      totalProducts: dto.totalProducts,
      totalCustomers: dto.totalCustomers,
      createdAt: dto.createdAt,
      actions: {
        canEdit: dto.actions.canEdit,
        canDelete: dto.actions.canDelete,
        canActivate: dto.actions.canActivate,
      },
    };
  }
}
